import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import Button, { ButtonTypes } from 'devextreme-react/button';
import Form, { Item, Label, ButtonItem, ButtonOptions, RequiredRule, EmailRule } from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';

import { useAuth } from '../../../contexts/auth';
import { ThemeContext } from '../../../theme/theme';

import './LoginForm.scss';
import { customerGroupsRequest, loginRequest } from '../../../api/requests';
import { customersToSession, setAppToken, setFirmName, setImageURL, setUserName, setVendorId } from '../../../api/sessions';
import { useTranslation } from 'react-i18next';
import { useCustomerContext } from '../../../contexts/customers';
import { appInfo } from '../../../app-info';
import ReCAPTCHA from "react-google-recaptcha";
import { getVendorIdCookie, setVendorIdCookie } from '../../../api/cookies';


function getButtonStylingMode(theme: string | undefined): ButtonTypes.ButtonStyle {
  return theme === 'dark' ? 'outlined' : 'contained';
}


export const LoginForm = ({ resetLink, createAccountLink }) => {
  const { t } = useTranslation();
  const [verified, setVerified] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const themeContext = useContext(ThemeContext);

  // vendorId ve rememberMe için state ekleyelim
  const [vendorId, setVendorrId] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // Default olarak işaretli

  // Sayfa yüklendiğinde vendorId çerezini al ve rememberMe'yi ayarla
  useEffect(() => {
    const savedVendorId = getVendorIdCookie();
    if (savedVendorId) {
      setVendorrId(savedVendorId);
      setRememberMe(true); // Eğer çerez varsa "Beni Hatırla" seçili olsun
    }
  }, []);

  const formData = useRef({ userName: "", password: "" });

  const onChange = () => {
    setVerified(true);
  };

  async function onSubmit (e) {
    e.preventDefault();
    if (!verified) {
      notify(t("pleaseVerifyRecaptcha") || "Please verify that you are not a robot.", "warning", 3000);
      return;
    }

    const { userName, password } = formData.current;
    setLoading(true);
    try {
      const response = await loginRequest(userName, password, vendorId);
      console.log("test")

      if (response.data.resCode == 200) {
        var token = response.data.salesUsers[0]["token"];
        setAppToken(token);
        setVendorId(vendorId);
        setUserName(userName);
        setFirmName(response.data.salesUsers[0]["erpFirmName"] ?? "");
        setImageURL(response.data.salesUsers[0]["imageUrl"] ?? "");


        // Eğer rememberMe seçiliyse vendorId'yi çerez olarak kaydet
        if (rememberMe) {
          setVendorIdCookie(vendorId);
        }

        const result = await signIn(userName, password);
        notify(t("loginSuccessfull"), "success", 1500);
        if (!result.isOk) {
          notify(result.message, "error", 3000);
        }
      } else {
        notify(response.data.resMessage, "error", 3000);
      }
    } catch (error) {
      notify("Bir hata oluştu. Lütfen tekrar deneyin.", "error", 3000);
    } finally {
      setLoading(false);
    }
  }

  const onCreateAccountClick = useCallback(() => {
    navigate(createAccountLink);
  }, [navigate]);

  const userNameEditorOptions = { stylingMode: "filled", placeholder: t("userName") };
  const vendorIdEditorOptions = {
    stylingMode: "filled",
    placeholder: t("vendorID"),
    value: vendorId, // State içindeki değer
    onValueChanged: (e) => setVendorrId(e.value), // Kullanıcı değişiklik yapınca güncelle
  };
  const passwordEditorOptions = { stylingMode: "filled", placeholder: t("password"), mode: "password" };
  const rememberMeEditorOptions = {
    text: t("rememberMe"),
    value: rememberMe, // Varsayılan olarak seçili yap
    onValueChanged: (e) => setRememberMe(e.value), // Kullanıcı değiştirirse güncelle
    elementAttr: { class: "form-text" }
  };

  return (
    <form className="login-form" onSubmit={onSubmit}>
      <Form formData={formData.current} disabled={loading} showColonAfterLabel showRequiredMark={false}>
        <Item dataField={"vendorId"} editorType={"dxTextBox"} editorOptions={vendorIdEditorOptions}>
          <RequiredRule message="Vendor ID is required" />
          <Label visible={false} />
        </Item>
        <Item dataField={"userName"} editorType={"dxTextBox"} editorOptions={userNameEditorOptions}>
          <RequiredRule message="User Name is required" />
          <Label visible={false} />
        </Item>
        <Item dataField={"password"} editorType={"dxTextBox"} editorOptions={passwordEditorOptions}>
          <RequiredRule message="Password is required" />
          <Label visible={false} />
        </Item>
        <Item dataField="rememberMe" editorType="dxCheckBox" editorOptions={rememberMeEditorOptions}>
          <Label visible={false} />
        </Item>
        <Item>
          <ReCAPTCHA sitekey="6LdhPQssAAAAAJpA3kRT-2J8GbqbiS9Uj7dWf_aI" onChange={onChange} />
        </Item>
        <ButtonItem>
          <ButtonOptions width="100%" type="default" useSubmitBehavior>
            <span className="dx-button-text">
              {loading ? <LoadIndicator width="24px" height="24px" visible /> : t("signIn")}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
      <div className="reset-link hidden">
        <Link to={resetLink}>{t("forgotPassword")}</Link>
      </div>

      <Button
        className="btn-create-account"
        text={t("createAccount")}
        width="100%"
        visible={false}
        onClick={onCreateAccountClick}
        stylingMode={getButtonStylingMode(themeContext?.theme)}
      />
    </form>
  );
};
